import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Calendar, Clock, MapPin, Users, DollarSign, MessageSquare, Crown } from 'lucide-react';

const ConciergePage = () => {
  const navigate = useNavigate();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    city: 'Miami',
    venue_type: 'club',
    budget: '',
    num_guests: 2,
    special_requests: '',
    request_type: 'custom'
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const quickRequests = [
    {
      id: 'vip_table_tonight',
      title: 'VIP Table Tonight',
      description: 'Need a table at a top venue tonight',
      icon: Crown,
      gradient: 'from-gold via-gold-deep to-gold-muted'
    },
    {
      id: 'birthday',
      title: 'Birthday Celebration',
      description: 'Plan an unforgettable birthday experience',
      icon: Sparkles,
      gradient: 'from-purple-500 via-pink-500 to-burgundy'
    },
    {
      id: 'romantic_dinner',
      title: 'Romantic Dinner',
      description: 'Reserve the perfect intimate dining experience',
      icon: MessageSquare,
      gradient: 'from-red-500 via-burgundy to-red-900'
    },
    {
      id: 'custom',
      title: 'Custom Request',
      description: 'Tell us what you need',
      icon: Sparkles,
      gradient: 'from-gold-muted via-gold to-gold-deep'
    }
  ];

  const handleQuickRequest = (requestType) => {
    setSelectedRequest(requestType);
    setFormData({ ...formData, request_type: requestType });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concierge-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          user_id: 'user_1' // TODO: Replace with actual user ID from context
        })
      });

      const data = await response.json();
      if (data.success) {
        setSubmitted(true);
        setTimeout(() => {
          navigate(-1);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting concierge request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-espresso to-black flex items-center justify-center p-4">
        <div className="glass-panel-luxury text-center p-8 max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-gold via-gold-deep to-gold-muted flex items-center justify-center gold-glow animate-pulse">
            <Sparkles className="text-black" size={40} />
          </div>
          <h2 className="text-3xl font-cinzel gold-text-luxury mb-4">Request Received</h2>
          <p className="text-cream text-lg mb-2">Your request has been sent to NearU Concierge.</p>
          <p className="text-cream/70">A specialist will assist you shortly.</p>
        </div>
      </div>
    );
  }

  if (!selectedRequest) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-espresso to-black pb-24">
        {/* Header */}
        <div className="glass-panel-luxury sticky top-0 z-10 border-b border-gold/10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="gold-button-3d p-2 rounded-lg"
            >
              <ArrowLeft size={24} />
            </button>
            <Sparkles size={28} className="text-gold" />
            <h1 className="text-2xl font-cinzel gold-text-luxury">NearU Concierge</h1>
          </div>
        </div>

        {/* Hero Section */}
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <div className="glass-panel-luxury p-8 mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-gold via-gold-deep to-gold-muted flex items-center justify-center gold-glow">
              <Crown className="text-black" size={40} />
            </div>
            <h2 className="text-3xl font-cinzel gold-text-luxury mb-4">
              Your Personal Nightlife Concierge
            </h2>
            <p className="text-cream/80 text-lg max-w-2xl mx-auto">
              Let our experts handle the details. From last-minute VIP tables to custom celebrations,
              we'll make it happen.
            </p>
          </div>

          {/* Quick Request Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quickRequests.map((request) => {
              const IconComponent = request.icon;
              return (
                <div
                  key={request.id}
                  onClick={() => handleQuickRequest(request.id)}
                  className="glass-panel-luxury p-6 cursor-pointer hover:scale-105 transition-all duration-300 group"
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${request.gradient} flex items-center justify-center shadow-lg group-hover:shadow-2xl transition-shadow`}>
                    <IconComponent className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-cormorant gold-text-luxury mb-2">{request.title}</h3>
                  <p className="text-cream/70">{request.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-espresso to-black pb-24">
      {/* Header */}
      <div className="glass-panel-luxury sticky top-0 z-10 border-b border-gold/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => setSelectedRequest(null)}
            className="gold-button-3d p-2 rounded-lg"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-cinzel gold-text-luxury">
            {quickRequests.find(r => r.id === selectedRequest)?.title || 'Concierge Request'}
          </h1>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto p-6 mt-6">
        <div className="glass-panel-luxury p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <Calendar size={18} className="inline mr-2" />
                Date *
              </label>
              <input
                type="date"
                required
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
              />
            </div>

            {/* Time */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <Clock size={18} className="inline mr-2" />
                Time *
              </label>
              <input
                type="time"
                required
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <MapPin size={18} className="inline mr-2" />
                City *
              </label>
              <select
                required
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
              >
                <option value="Miami">Miami</option>
                <option value="New York">New York</option>
                <option value="Los Angeles">Los Angeles</option>
                <option value="Las Vegas">Las Vegas</option>
                <option value="London">London</option>
                <option value="Dubai">Dubai</option>
              </select>
            </div>

            {/* Venue Type */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">Venue Type *</label>
              <select
                required
                value={formData.venue_type}
                onChange={(e) => setFormData({ ...formData, venue_type: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
              >
                <option value="club">Nightclub</option>
                <option value="lounge">Lounge</option>
                <option value="restaurant">Restaurant</option>
                <option value="beach_club">Beach Club</option>
                <option value="rooftop">Rooftop Bar</option>
              </select>
            </div>

            {/* Number of Guests */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <Users size={18} className="inline mr-2" />
                Number of Guests *
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.num_guests}
                onChange={(e) => setFormData({ ...formData, num_guests: parseInt(e.target.value) })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
              />
            </div>

            {/* Budget */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <DollarSign size={18} className="inline mr-2" />
                Budget (Optional)
              </label>
              <select
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all"
              >
                <option value="">Select budget range</option>
                <option value="$500-$1000">$500 - $1,000</option>
                <option value="$1000-$2500">$1,000 - $2,500</option>
                <option value="$2500-$5000">$2,500 - $5,000</option>
                <option value="$5000+">$5,000+</option>
              </select>
            </div>

            {/* Special Requests */}
            <div>
              <label className="block text-gold mb-2 font-cormorant text-lg">
                <MessageSquare size={18} className="inline mr-2" />
                Special Requests
              </label>
              <textarea
                value={formData.special_requests}
                onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                rows={4}
                className="w-full bg-black/40 border border-gold/20 rounded-lg px-4 py-3 text-cream focus:outline-none focus:border-gold/60 transition-all resize-none"
                placeholder="Champagne preference, seating location, celebration details..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full gold-button-3d py-4 rounded-xl font-cormorant text-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : 'Submit to Concierge'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConciergePage;